import { useState, useEffect, useRef } from 'react';
import { generateBCP } from '../services/api';
import type { BCPFormData } from '../types';
import type { BCPGenerationRequest, BCPGenerationResponse, ExtractedCompanyInfo, UploadData } from '../types/bcp-generation';
import type { BCPNavigationState } from '../types/navigation';
import { transformBCPDocument } from '../utils/bcp-transform';
import { createApiRequest } from '../utils/apiResponse';
import { useAppNavigation } from '../utils/navigation';
import { logger } from '../utils/logger';

interface UseBCPGenerationOptions {
  isEditMode: boolean;
  uploadedFiles: any[];
  apiResponse?: UploadData;
  companyInfo?: BCPNavigationState['companyInfo'];
  restorePolicyData?: Record<string, string>;
}

interface BCPGenerationState {
  isLoading: boolean;
  formData: BCPFormData;
  hasGenerated: boolean;
  error: string | null;
}

export const useBCPGeneration = ({
  isEditMode,
  uploadedFiles,
  apiResponse,
  companyInfo,
  restorePolicyData
}: UseBCPGenerationOptions) => {
  const navigation = useAppNavigation();
  const hasExecutedRef = useRef(false);
  const initializedRef = useRef(false);

  const [state, setState] = useState<BCPGenerationState>({
    isLoading: false,
    formData: {} as BCPFormData,
    hasGenerated: false,
    error: null
  });

  const generateBCPFromFiles = async () => {
    logger.info('Starting BCP generation from files', {
      component: 'useBCPGeneration',
      action: 'generateBCPFromFiles',
      data: {
        filesCount: uploadedFiles.length,
        hasApiResponse: !!apiResponse,
        hasCompanyInfo: !!companyInfo
      }
    });

    // 二重実行防止
    if (hasExecutedRef.current || state.isLoading) {
      logger.warn('BCP generation blocked - already in progress or executed', {
        component: 'useBCPGeneration',
        action: 'generateBCPFromFiles',
        data: { hasExecuted: hasExecutedRef.current, isLoading: state.isLoading }
      });
      return;
    }

    hasExecutedRef.current = true;
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      // apiResponseもcompanyInfoもない場合はエラー
      if (!apiResponse && !companyInfo) {
        throw new Error('No API response data or company info available');
      }

      // Extract information from upload data with proper typing
      const extractedInfo: ExtractedCompanyInfo = apiResponse?.extracted_info || {};
      const mergedInfo: ExtractedCompanyInfo = apiResponse?.merged_extracted_info || {};

      // companyInfoが存在する場合は優先して使用
      const extractedAddress = companyInfo?.location || mergedInfo.address || extractedInfo.address || '';
      const extractedClients = mergedInfo.clients || extractedInfo.clients || [];
      const extractedSuppliers = mergedInfo.suppliers || extractedInfo.suppliers || [];
      const extractedTel = mergedInfo.tel || extractedInfo.tel || '';
      const extractedFax = mergedInfo.fax || extractedInfo.fax || '';
      const extractedCompanyName = companyInfo?.companyName || mergedInfo.company_name || extractedInfo.company_name || '';
      const extractedEmployees = companyInfo?.employeeCount || mergedInfo.employees || extractedInfo.employees || '';
      const extractedCapital = mergedInfo.capital || extractedInfo.capital || '';
      const extractedEstablished = mergedInfo.established || extractedInfo.established || '';
      const extractedBusinessContent = companyInfo?.businessDescription || mergedInfo.business_content || extractedInfo.business_content || '';
      const extractedCriticalBusiness = companyInfo?.criticalBusiness || mergedInfo.main_business || extractedInfo.main_business || '';
      const extractedRepresentatives = mergedInfo.representatives || extractedInfo.representatives || [];
      const extractedRepresentative = mergedInfo.representative || extractedInfo.representative || '';
      const extractedExecutives = mergedInfo.executives || extractedInfo.executives || [];
      const extractedFinancialData = mergedInfo.financial_data || extractedInfo.financial_data || {};


      // ファイル名を正しく取得
      const filename = apiResponse?.filename || extractedCompanyName || 'Sample Company';
      const tenant_id = apiResponse?.tenant_id || 'default';

      // Use extracted address or a more generic fallback
      let location = extractedAddress;
      if (!location || location.trim().length === 0) {
        location = '東京都';
      }

      const requestData: BCPGenerationRequest = {
        company_name: extractedCompanyName || filename.replace('.pdf', '').split(' ')[0] || 'Sample Company',
        business_type: 'Manufacturing',
        location: location,
        tenant_id: tenant_id,
        extracted_info: {
          company_name: extractedCompanyName,
          address: extractedAddress,
          clients: extractedClients,
          suppliers: extractedSuppliers,
          tel: extractedTel,
          fax: extractedFax,
          employees: extractedEmployees,
          capital: extractedCapital,
          established: extractedEstablished,
          business_content: extractedBusinessContent,
          main_business: extractedCriticalBusiness,
          representatives: extractedRepresentatives,
          // representatives can be array of strings or array of objects {name, title}
          representative: extractedRepresentative || (extractedRepresentatives.length > 0
            ? typeof extractedRepresentatives[0] === 'string'
              ? extractedRepresentatives[0]
              : extractedRepresentatives[0]?.name || ''
            : ''),
          executives: extractedExecutives,
          financial_data: extractedFinancialData,
          // Add company_info from restore-policy data only if it exists
          ...(restorePolicyData?.company_info && {
            company_info: restorePolicyData.company_info
          })
        }
      };
      
      
      const responseData = await createApiRequest({
        request: () => generateBCP(requestData),
        onSuccess: (data) => {
        },
        onError: (error) => {
          setState(prev => ({ ...prev, error: error.userMessage || error.message }));
        }
      });
      
      if (responseData) {
        let convertedFormData: BCPFormData;

        if (responseData && typeof responseData === 'object' && 'bcp_document' in responseData) {
          // Use the new transform utility instead of inline processing
          convertedFormData = transformBCPDocument((responseData as any).bcp_document, {
            extractedInfo: mergedInfo,
            filename: filename
          });

          // Override stockpilePlan with generated value from API if available
          if ('stockpile_plan' in responseData && (responseData as any).stockpile_plan) {
            convertedFormData.stockpilePlan = (responseData as any).stockpile_plan;
            logger.info('Using stockpile_plan from API response', {
              component: 'useBCPGeneration',
              action: 'transformResponse',
              data: { stockpilePlanLength: convertedFormData.stockpilePlan.length }
            });
          }
        } else {
          // フォールバック: デフォルト値を設定
          convertedFormData = {
            companyInfo: '企業名：\n代表者：\n所在地：\nTEL：\nFAX：\n事業内容：\n従業員数：\n主な取引先：',
            continuityPolicy: '安全確保方針：\n早期復旧方針：\n社会的責任：',
            riskAnalysis: '1. 自然災害（地震、台風、水害等）\n2. 人為的災害（火災、爆発等）\n3. 感染症\n4. その他',
            criticalBusinesses: '重要業務一覧：\n優先度の根拠：',
            recoveryPlan: '業務復旧目標：\n必要資源：',
            financialPlan: '必要資金：\n資金調達方法：\n保険の活用：',
            stockpilePlan: '備蓄品リスト：\n保管場所：\n管理責任者：',
            alternativeMeasures: '代替手段：\nリモートワーク体制：\n代替拠点：',
            activationCriteria: 'BCP発動基準：\n発動責任者：\n発動手順：',
            emergencyOrganization: '初動対応フロー：\n1. 安全確認\n2. 被害状況確認',
            rolesResponsibilities: '組織体制：\n統括責任者：\n情報担当：',
            communicationMethods: '連絡手段：\n緊急連絡網：\n連絡先リスト：',
            regularTraining: '定期訓練計画：\n実施頻度：\n評価方法：',
            bcpReview: '見直しルール：\n見直し頻度：\n見直し担当者：',
            employeeEducation: '従業員教育計画：\n新入社員研修：\n定期研修：'
          };
        }
        
        setState(prev => ({
          ...prev,
          formData: convertedFormData,
          isLoading: false,
          hasGenerated: true
        }));
      } else {
        throw new Error('No response data received');
      }
    } catch (error) {
      // Error is already handled in createApiRequest
      setState(prev => ({
        ...prev,
        isLoading: false
      }));
      throw error;
    }
  };

  useEffect(() => {
    // 初期化済みの場合はスキップ
    if (initializedRef.current) {
      return;
    }

    initializedRef.current = true;

    // 編集モードの場合は生成をスキップ
    if (isEditMode) {
      return;
    }

    // uploadedFilesもcompanyInfoもない場合はアップロードページにリダイレクト
    if (uploadedFiles.length === 0 && !companyInfo) {
      if (!navigation.requireUploadedFiles(uploadedFiles)) {
        return;
      }
    }

    // まだ生成していない場合のみ実行
    if (!hasExecutedRef.current) {
      generateBCPFromFiles();
    }
  }, []);

  return {
    ...state,
    regenerate: () => {
      hasExecutedRef.current = false;
      generateBCPFromFiles();
    }
  };
};